import { useQuery, useQueryClient } from '@tanstack/react-query'
import { type ReactNode, useState } from 'react'
import { settingsCopy } from '@/copy'
import { Alert } from '@/ui/Alert/Alert'
import { Avatar } from '@/ui/Avatar/Avatar'
import { Button } from '@/ui/Button/Button'
import { Card } from '@/ui/Card/Card'
import { Chip } from '@/ui/Chip/Chip'
import { EmptyState } from '@/ui/EmptyState/EmptyState'
import { Field, Input } from '@/ui/Field/Field'
import { Skeleton } from '@/ui/Loading/Loading'
import { useSkeletonDelay } from '@/ui/Loading/useSkeletonDelay'
import { Inline } from '@/ui/layout/Inline'
import { Stack } from '@/ui/layout/Stack'
import { Text } from '@/ui/layout/Text'
import { ProgressBar } from '@/ui/ProgressBar/ProgressBar'
import { SectionHeader } from '@/ui/SectionHeader/SectionHeader'
import { Table } from '@/ui/Table/Table'
import { agentDefsApi } from '../api/agents'
import { workspacesApi } from '../api/workspaces'
import { useFeatures } from '../hooks/queries'
import { useAsyncSubmit } from '../hooks/useAsyncSubmit'
import type { AgentUsageSummary, SpendCapInfo } from '../types/api'
import { formatNumber, formatUsd } from '../utils/format'
import { RefreshFailed } from './RefreshFailed'
import styles from './UsagePanel.module.css'

interface Props {
  wsId: string
  /** Shows the spend-cap editor (admin+). Read-only display otherwise. */
  canManage?: boolean
  /** A toolbar row under the section header (the workspace picker). */
  toolbar?: ReactNode
}

function SpendCapRow({
  wsId,
  canManage,
}: {
  wsId: string
  canManage: boolean
}) {
  const queryClient = useQueryClient()
  const capQuery = useQuery<SpendCapInfo>({
    queryKey: ['spend-cap', wsId],
    queryFn: () => workspacesApi.getSpendCap(wsId),
    enabled: !!wsId,
    staleTime: 60_000,
  })
  const cap = capQuery.data
  const showSkeleton = useSkeletonDelay(capQuery.isPending)
  const [draft, setDraft] = useState('')
  const { loading, error, submit } = useAsyncSubmit()

  // Seed the draft from the fetched cap during render (React's adjusting-
  // state-when-data-changes pattern) instead of an effect, so there's no
  // paint with a stale value and no cascading re-render.
  const [seededCapUsd, setSeededCapUsd] = useState(cap?.cap_usd)
  if (cap?.cap_usd !== seededCapUsd) {
    setSeededCapUsd(cap?.cap_usd)
    setDraft(cap?.cap_usd != null ? String(cap.cap_usd) : '')
  }

  // Only the button that fired shows pending (component-states).
  const [firing, setFiring] = useState<'set' | 'clear' | null>(null)
  const save = (value: number | null) => {
    setFiring(value === null ? 'clear' : 'set')
    return submit(async () => {
      await workspacesApi.setSpendCap(wsId, value)
      await queryClient.invalidateQueries({ queryKey: ['spend-cap', wsId] })
    }, settingsCopy.usage.capFailed)
  }

  let body: ReactNode
  if (capQuery.isPending) {
    body = showSkeleton ? <Skeleton lines={3} /> : null
  } else if (!cap) {
    body = (
      <Alert tone="danger">
        <Inline gap="space-8" align="baseline" wrap>
          <Text>{settingsCopy.loadFailed.spendCap}</Text>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void capQuery.refetch()}
          >
            {settingsCopy.retry}
          </Button>
        </Inline>
      </Alert>
    )
  } else {
    const overCap = cap.cap_usd != null && cap.month_spend_usd >= cap.cap_usd
    body = (
      <Stack gap="space-8">
        {capQuery.isError && (
          <RefreshFailed
            message={settingsCopy.refreshFailed.spendCap}
            onRetry={() => void capQuery.refetch()}
          />
        )}
        {cap.cap_usd != null ? (
          <ProgressBar
            label={settingsCopy.usage.monthlyCap}
            value={Math.min(cap.month_spend_usd, cap.cap_usd)}
            max={cap.cap_usd}
            valueLabel={settingsCopy.usage.capValue(
              formatUsd(cap.month_spend_usd, 2),
              formatUsd(cap.cap_usd, 2),
            )}
            tone={overCap ? 'danger' : 'accent'}
            message={overCap ? settingsCopy.usage.capReached : undefined}
          />
        ) : (
          <Inline gap="space-8" justify="between">
            <Text tone="text-mute">{settingsCopy.usage.monthlyCap}</Text>
            <Text numeric>
              {settingsCopy.usage.noCap(formatUsd(cap.month_spend_usd, 2))}
            </Text>
          </Inline>
        )}
        {canManage && (
          <Inline gap="space-8" align="end">
            <Field
              label={settingsCopy.usage.capInput}
              className={styles.capInput}
            >
              <Input
                type="number"
                min="0"
                step="1"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
              />
            </Field>
            <Button
              variant="outline"
              loading={loading && firing === 'set'}
              disabled={
                (loading && firing !== 'set') ||
                !draft.trim() ||
                Number(draft) <= 0
              }
              onClick={() => void save(Number(draft))}
            >
              {settingsCopy.usage.set}
            </Button>
            {cap.cap_usd != null && (
              <Button
                variant="ghost"
                loading={loading && firing === 'clear'}
                disabled={loading && firing !== 'clear'}
                onClick={() => void save(null)}
              >
                {settingsCopy.usage.clear}
              </Button>
            )}
          </Inline>
        )}
        {error && <Alert tone="danger">{error}</Alert>}
      </Stack>
    )
  }

  return (
    <Card variant="flat" className={styles.cap} aria-busy={capQuery.isPending}>
      {body}
    </Card>
  )
}

export function UsagePanel({ wsId, canManage = false, toolbar }: Props) {
  const features = useFeatures()
  const usageQuery = useQuery<AgentUsageSummary[]>({
    queryKey: ['usage', wsId],
    queryFn: () => agentDefsApi.listWorkspaceUsage(wsId),
    enabled: !!wsId,
    staleTime: 60_000,
  })
  const { data: usage = [], isLoading, isError } = usageQuery
  const showSkeleton = useSkeletonDelay(isLoading)

  const totals = usage.reduce(
    (acc, r) => ({
      input: acc.input + r.total_input_tokens,
      output: acc.output + r.total_output_tokens,
      cached: acc.cached + r.total_cached_tokens,
      total: acc.total + r.total_input_tokens + r.total_output_tokens,
      cost: acc.cost + r.total_estimated_cost_usd,
    }),
    { input: 0, output: 0, cached: 0, total: 0, cost: 0 },
  )

  return (
    <section className={styles.root}>
      <SectionHeader rule trailingSpacing="section">
        {settingsCopy.usage.heading}
      </SectionHeader>
      <Stack gap="space-12">
        {toolbar}

        {/* Token and cost rows below are unconditional — cost estimation runs in
          every deployment. Only the cap, which needs enforcement to mean
          anything, is gated. */}
        {wsId && features.spend_caps && (
          <SpendCapRow wsId={wsId} canManage={canManage} />
        )}

        {wsId && isError && usageQuery.data !== undefined && (
          <RefreshFailed
            message={settingsCopy.refreshFailed.usage}
            onRetry={() => void usageQuery.refetch()}
          />
        )}
        {!wsId ? (
          <EmptyState
            variant="inline"
            description={settingsCopy.usage.noWorkspaces}
          />
        ) : (
          <Table
            caption={settingsCopy.usage.caption}
            rows={usage}
            getRowId={(r) => r.agent_id}
            loading={showSkeleton}
            error={
              isError && usageQuery.data === undefined ? (
                <Alert tone="danger">
                  <Inline gap="space-8" align="baseline" wrap>
                    <Text>{settingsCopy.loadFailed.usage}</Text>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => void usageQuery.refetch()}
                    >
                      {settingsCopy.retry}
                    </Button>
                  </Inline>
                </Alert>
              ) : undefined
            }
            empty={
              isLoading ? null : (
                <EmptyState
                  variant="inline"
                  description={settingsCopy.usage.empty}
                />
              )
            }
            footer={
              usage.length
                ? {
                    agent: settingsCopy.usage.total,
                    input: formatNumber(totals.input),
                    output: formatNumber(totals.output),
                    cached: formatNumber(totals.cached),
                    total: formatNumber(totals.total),
                    cost: formatUsd(totals.cost, 4),
                  }
                : undefined
            }
            columns={[
              {
                key: 'agent',
                header: settingsCopy.usage.columns.agent,
                render: (r) => (
                  <Inline gap="space-8" wrap={false}>
                    <Avatar name={r.agent_name} kind="agent" size="md" />
                    <Inline gap="space-8" align="baseline" wrap={false}>
                      <Text>{r.agent_name}</Text>
                      <Text variant="code" tone="text-mute">
                        {r.agent_id}
                      </Text>
                    </Inline>
                  </Inline>
                ),
              },
              {
                key: 'model',
                header: settingsCopy.usage.model,
                render: (r) => <Chip variant="kind" label={r.model} />,
              },
              {
                key: 'input',
                header: settingsCopy.usage.columns.input,
                align: 'end',
                render: (r) => formatNumber(r.total_input_tokens),
              },
              {
                key: 'output',
                header: settingsCopy.usage.columns.output,
                align: 'end',
                render: (r) => formatNumber(r.total_output_tokens),
              },
              {
                key: 'cached',
                header: settingsCopy.usage.columns.cached,
                align: 'end',
                render: (r) => formatNumber(r.total_cached_tokens),
              },
              {
                key: 'total',
                header: settingsCopy.usage.columns.total,
                align: 'end',
                render: (r) =>
                  formatNumber(r.total_input_tokens + r.total_output_tokens),
              },
              {
                key: 'cost',
                header: settingsCopy.usage.columns.cost,
                align: 'end',
                render: (r) => formatUsd(r.total_estimated_cost_usd, 4),
              },
            ]}
          />
        )}
      </Stack>
    </section>
  )
}
